/**
 * The application's navigation, as data.
 *
 * WHY THIS IS DATA AND NOT JSX
 * ---------------------------
 * Three things need to know the navigation, and none of them is a component:
 * the sidebar that draws it, the command palette that searches it, and the
 * tests that assert what exists. If the menu were markup, the palette would
 * have to scrape it and the tests would have to render it. So the menu is a
 * plain list, and every consumer reads the same answer.
 *
 * `built` IS AN HONESTY FLAG, NOT A FEATURE SWITCH
 * -----------------------------------------------
 * `built: false` means there is no screen. It does NOT mean "render something
 * plausible" — an unbuilt route shows a plain "not built yet" state, because a
 * menu item leading to a convincing empty page is worse than one that admits
 * the work is outstanding. The alternative, hiding the items until they work,
 * would make the plan invisible and the sidebar lie about what the app does.
 *
 * ALIASES ARE LOAD-BEARING
 * -----------------------
 * The routes that already work (`#/documents`, `#/items`, `#/contacts`) are recorded as ALIASES of their canonical homes rather than
 * being dropped. Those hashes are in the E2E suite, in bookmarks and in muscle
 * memory; an in-app rename must not break them, nor silently change which
 * screen a link means.
 */
export type NavGroupId =
  | 'overview'
  | 'sales'
  | 'purchases'
  | 'inventory'
  | 'contacts'
  | 'payments'
  | 'reports'
  | 'settings'
  | 'help';

export type NavIcon =
  | 'dashboard'
  | 'sales'
  | 'purchases'
  | 'inventory'
  | 'contacts'
  | 'payments'
  | 'reports'
  | 'settings'
  | 'help';

export interface NavItem {
  /** Stable key, also the command id in the palette. */
  readonly id: string;
  readonly label: string;
  /** The canonical hash route. What new links should point at. */
  readonly href: string;
  readonly group: NavGroupId;
  /** True only when a real screen exists behind this route. */
  readonly built: boolean;
  /** Older routes that must keep working, and keep meaning this screen. */
  readonly aliases?: readonly string[];
  /** Extra words the command palette should match on. */
  readonly keywords?: readonly string[];
}

export interface NavGroup {
  readonly id: NavGroupId;
  readonly label: string;
  readonly icon: NavIcon;
  readonly items: readonly NavItem[];
}

/** A destination with no screen behind it yet. */
function planned(
  id: string,
  label: string,
  href: string,
  group: NavGroupId,
  keywords?: readonly string[],
): NavItem {
  return { id, label, href, group, built: false, ...(keywords ? { keywords } : {}) };
}

/** The destinations that have real screens behind them, declared once. */
const QUOTATIONS: NavItem = {
  id: 'sales.quotations',
  label: 'Quotations',
  href: '#/sales/quotations',
  group: 'sales',
  built: true,
  keywords: ['quote', 'estimate', 'enquiry'],
};

const SALES_ORDERS: NavItem = {
  id: 'sales.orders',
  label: 'Sales Orders',
  href: '#/sales/orders',
  group: 'sales',
  built: true,
  keywords: ['so', 'order', 'customer purchase order'],
};

const DELIVERY_NOTES: NavItem = {
  id: 'sales.deliveries',
  label: 'Delivery Notes',
  href: '#/sales/deliveries',
  group: 'sales',
  built: true,
  keywords: ['dn', 'delivery', 'dispatch'],
};

/**
 * Credit Notes and Sales Returns.
 *
 * TWO separate destinations, because the two documents are genuinely different
 * things and a shopkeeper filing paper at year end needs to find one without the
 * other in the way. Merging them into a "Post-sale" list is the same mistake
 * that put quotations under Invoices in 1.6.x.
 */
const CREDIT_NOTES: NavItem = {
  id: 'sales.credit-notes',
  label: 'Credit Notes',
  href: '#/sales/credit-notes',
  group: 'sales',
  built: true,
  keywords: ['crn', 'credit', 'adjustment', 'reduce'],
};

const SALES_RETURNS: NavItem = {
  id: 'sales.returns',
  label: 'Sales Returns',
  href: '#/sales/returns',
  group: 'sales',
  built: true,
  keywords: ['srn', 'return', 'restock', 'goods back'],
};

const INVOICES: NavItem = {
  id: 'sales.invoices',
  label: 'Invoices',
  href: '#/sales/invoices',
  group: 'sales',
  built: true,
  // The routes the document screen has always lived at. `#/documents/new`
  // matches as a child of `#/documents`, so the editor keeps working untouched.
  aliases: ['#/documents'],
  keywords: ['tax invoice', 'sales invoice', 'bill', 'raise invoice'],
};

const PRODUCTS: NavItem = {
  id: 'inventory.products',
  label: 'Products',
  href: '#/inventory/products',
  group: 'inventory',
  built: true,
  aliases: ['#/items'],
  keywords: ['items', 'catalogue', 'goods'],
};

const CUSTOMERS: NavItem = {
  id: 'contacts.customers',
  label: 'Customers',
  href: '#/contacts/customers',
  group: 'contacts',
  built: true,
  aliases: ['#/contacts'],
  // 'debtors' is deliberately NOT here even though it describes a customer. It
  // is the word people type for the RECEIVABLES report, and carrying it on both
  // made "debtors" resolve to Customers purely because Contacts sits earlier in
  // the menu — two equal keyword matches cannot be told apart by ranking.
  keywords: ['buyers', 'parties'],
};

const RECEIPTS: NavItem = {
  id: 'payments.receipts',
  label: 'Receipts',
  href: '#/payments/receipts',
  group: 'payments',
  built: true,
  keywords: ['receive', 'money in', 'rcp', 'customer payment'],
};

const SUPPLIERS: NavItem = {
  id: 'contacts.suppliers',
  label: 'Suppliers',
  href: '#/contacts/suppliers',
  group: 'contacts',
  built: true,
  keywords: ['vendors', 'parties', 'creditors'],
};

/**
 * Backup & Restore.
 *
 * A RELEASE BLOCKER, and it sits here as a real destination rather than under a
 * "coming soon" heading because the service behind it has existed for a long time
 * and been unreachable the whole time. A feature a user cannot open is not a
 * feature, and listing it as planned while it is one click away would be worse.
 */
const BUSINESS_PROFILE: NavItem = {
  id: 'settings.business',
  label: 'Business Profile',
  href: '#/settings/business',
  group: 'settings',
  built: true,
  aliases: ['#/business'],
  keywords: ['gstin', 'letterhead', 'bank', 'company', 'my business', 'gst'],
};

const BACKUP_RESTORE: NavItem = {
  id: 'settings.backup',
  label: 'Backup & Restore',
  href: '#/settings/backup',
  group: 'settings',
  built: true,
  aliases: ['#/backup'],
  keywords: ['backup', 'restore', 'export', 'snapshot', 'download', 'csv', 'safety net'],
};

const CLOUD_SYNC: NavItem = {
  id: 'settings.sync',
  label: 'Sync & Cloud',
  href: '#/settings/sync',
  group: 'settings',
  built: true,
  keywords: ['sync', 'cloud', 'devices', 'conflicts', 'multi-device'],
};

/**
 * Purchase Orders.
 *
 * The only `built` destination in the Purchases group, and the only one for now.
 * The other three stay `planned` on purpose: a Purchase Receipt, Purchase Invoice
 * or Supplier Payment is a real accounting step with real consequences, and marking
 * them built would claim a workflow this app does not have.
 *
 * Keywords deliberately EXCLUDE "customer purchase order". In 1.6.2 that phrase
 * meant a SALES-side order (`customerPurchaseOrders -> SALES_ORDER`), and reusing
 * be handed a purchase one — the exact name-based classification error this project
 * has already been bitten by once.
 */
const PURCHASE_ORDERS: NavItem = {
  id: 'purchases.orders',
  label: 'Purchase Orders',
  href: '#/purchases/orders',
  group: 'purchases',
  built: true,
  keywords: ['po', 'buy order', 'supplier order', 'order to supplier'],
};

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'dashboard',
    items: [planned('overview.dashboard', 'Dashboard', '#/dashboard', 'overview', ['home', 'summary', 'today'])],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: 'sales',
    items: [
      QUOTATIONS,
      SALES_ORDERS,
      DELIVERY_NOTES,
      INVOICES,
      CREDIT_NOTES,
      SALES_RETURNS,
    ],
  },
  {
    id: 'purchases',
    label: 'Purchases',
    icon: 'purchases',
    items: [
      PURCHASE_ORDERS,
      planned('purchases.invoices', 'Purchase Invoices', '#/purchases/invoices', 'purchases', ['bill', 'vendor bill']),
      planned('purchases.debit-notes', 'Debit Notes', '#/purchases/debit-notes', 'purchases', ['dbn', 'debit']),
      planned('purchases.returns', 'Purchase Returns', '#/purchases/returns', 'purchases', ['prn', 'return']),
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: 'inventory',
    items: [
      PRODUCTS,
      planned('inventory.stock', 'Stock', '#/inventory/stock', 'inventory', ['on hand', 'balance', 'ledger']),
      planned('inventory.adjustments', 'Stock Adjustments', '#/inventory/adjustments', 'inventory', [
        'adjust',
        'reconciliation',
      ]),
    ],
  },
  { id: 'contacts', label: 'Contacts', icon: 'contacts', items: [CUSTOMERS, SUPPLIERS] },
  {
    id: 'payments',
    label: 'Payments',
    icon: 'payments',
    items: [
      RECEIPTS,
      planned('payments.payments', 'Payments', '#/payments/payments', 'payments', ['pay', 'money out']),
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'reports',
    items: [
      planned('reports.sales', 'Sales', '#/reports/sales', 'reports'),
      planned('reports.purchases', 'Purchases', '#/reports/purchases', 'reports'),
      planned('reports.receivables', 'Receivables', '#/reports/receivables', 'reports', ['debtors', 'who owes']),
      planned('reports.payables', 'Payables', '#/reports/payables', 'reports', ['creditors']),
      planned('reports.gst', 'GST', '#/reports/gst', 'reports', ['gst1', 'gstr', 'tax summary', 'igst']),
      planned('reports.profit-loss', 'Profit & Loss', '#/reports/profit-loss', 'reports', ['p and l', 'cogs', 'margin']),
      planned('reports.stock', 'Stock', '#/reports/stock', 'reports'),
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    items: [
      BUSINESS_PROFILE,
      BACKUP_RESTORE,
      CLOUD_SYNC,
    ],
  },
  {
    id: 'help',
    label: 'Help',
    icon: 'help',
    items: [
      {
        id: 'help.main',
        label: 'Help Center',
        href: '#/help',
        group: 'help',
        built: true,
        keywords: ['help', 'docs', 'documentation', 'support', 'guide'],
      },
    ],
  },
];

/** Every item, flattened, in menu order. */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** A hash, plus whether it names a child of `base` (e.g. `new` under an id). */
function covers(base: string, route: string): boolean {
  return route === base || route.startsWith(`${base}/`);
}

/**
 * Is this the screen `item` stands for?
 *
 * Matches the canonical route, anything beneath it, and every alias — so
 * `#/documents/new` highlights "Invoices" exactly as `#/sales/invoices/new`
 * does. A menu that stops highlighting on the old URL after a rename is a menu
 * the user has to re-learn.
 */
export function isNavItemActive(item: NavItem, route: string): boolean {
  if (covers(item.href, route)) return true;
  return (item.aliases ?? []).some((alias) => covers(alias, route));
}

/** How strongly a route claims an item: its own prefix, or an alias prefix. */
function claimLength(item: NavItem, route: string): number {
  if (covers(item.href, route)) return item.href.length;
  for (const alias of item.aliases ?? []) {
    if (covers(alias, route)) return alias.length;
  }
  return -1;
}

/**
 * The item a route belongs to, or null.
 *
 * Longest claim wins, so a more specific child route is never swallowed by a
 * shorter one that merely happens to share a prefix.
 */
export function findNavItem(route: string): NavItem | null {
  let best: NavItem | null = null;
  let bestLength = -1;
  for (const item of NAV_ITEMS) {
    const length = claimLength(item, route);
    if (length > bestLength) {
      best = item;
      bestLength = length;
    }
  }
  return best;
}

/** The group a route belongs to, for sidebar highlighting. */
export function findNavGroup(route: string): NavGroupId | null {
  return findNavItem(route)?.group ?? null;
}

/** Where the app opens when there is no hash at all. */
export const DEFAULT_ROUTE = '#/sales/invoices';
